import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Search, X } from 'lucide-react'

/**
 * Full-screen mobile search sheet, mirroring Airbnb's "tap to expand" pattern:
 * a dimmed backdrop, a close button, a scrollable stack of accordion
 * {@link SearchSection} cards (passed as children), and a pinned footer with
 * "Clear all" and a brand-colored "Search" CTA.
 *
 * Rendered through a portal to `document.body` — the discovery headers use
 * `backdrop-blur`, which makes a `position: fixed` descendant resolve against
 * the header instead of the viewport. The portal sidesteps that containing-block
 * trap so the sheet always fills the screen. Locks body scroll and closes on
 * Escape.
 */
export function SearchSheet({
  open,
  onClose,
  onClear,
  onSubmit,
  children,
  title = 'Search',
}: {
  open: boolean
  onClose: () => void
  onClear: () => void
  onSubmit: () => void
  children: ReactNode
  title?: string
}) {
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[70] flex flex-col bg-stone-500/40 backdrop-blur-md"
    >
      <div className="flex shrink-0 items-center justify-end px-4 pt-4">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close search"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-stone-800 shadow-md transition hover:bg-stone-50"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 pb-4 pt-2">{children}</div>

      <div className="flex shrink-0 items-center justify-between gap-4 border-t border-stone-200 bg-white px-5 py-4">
        <button
          type="button"
          onClick={onClear}
          className="text-sm font-semibold text-stone-800 underline underline-offset-4 transition hover:text-stone-950"
        >
          Clear all
        </button>
        <button
          type="button"
          onClick={onSubmit}
          className="flex items-center gap-2 rounded-2xl bg-brand-700 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-brand-800"
        >
          <Search className="h-4 w-4" />
          Search
        </button>
      </div>
    </div>,
    document.body,
  )
}
